import { isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class DataLoaderService {
  private data: Map<string, unknown> = new Map();
  private loadingPromises: Map<string, Promise<unknown>> = new Map();
  private maxAgeTimeouts: Map<string, NodeJS.Timeout> = new Map();

  private platformId = inject(PLATFORM_ID);

  /**
   * Gets the data either from the store, or if not
   * found executes the function and adds the result to
   * the store.
   * If the loading function is executed, the resulting promise
   * is stored as well, and this promise is used in subsequent calls.
   * @param key identifies the data in the store
   * @param fn function to load the data
   * @param maxAge optional max age in ms for the data
   * @returns the loaded data
   */
  async get<T>(key: string, fn: () => Promise<T>, maxAge?: number) {
    if (this.data.has(key)) return <T>this.data.get(key);
    if (this.loadingPromises.has(key)) return <T>await this.loadingPromises.get(key);

    const promise = fn();
    this.loadingPromises.set(key, promise);

    try {
      const resp = await promise;
      if (resp !== undefined) {
        this.data.set(key, resp);
        if (maxAge && isPlatformBrowser(this.platformId)) {
          if (this.maxAgeTimeouts.has(key)) clearTimeout(this.maxAgeTimeouts.get(key));
          this.maxAgeTimeouts.set(
            key,
            setTimeout(() => {
              this.data.delete(key);
            }, maxAge)
          );
        }
      }
      this.loadingPromises.delete(key);
      return resp;
    } catch (err) {
      this.loadingPromises.delete(key);
      throw err;
    }
  }

  /**
   * Gets a value only if it is in the cache or already requested
   * @param key
   */
  async getFromCache<T>(key: string): Promise<T> {
    const oldData = this.data.get(key);
    if (oldData) return <T>oldData;

    const oldPromise = this.loadingPromises.get(key) as Promise<T>;
    if (oldPromise) return await oldPromise;
  }

  /**
   * Gets a value only if it is in the cache or already requested where the key just has to be part of the found key
   * @param key
   */
  async getFromCacheFuzzy<T>(key: string): Promise<T[]> {
    const keys = Array.from(this.data.keys()).filter(_key => _key.includes(key));
    if (!keys.length) return;
    return await Promise.all(keys.map(_key => <T>this.getFromCache(_key)));
  }

  /**
   * Removes data from the cache
   * @param key string
   */
  async remove(key: string) {
    this.data.delete(key);
    this.loadingPromises.delete(key);
  }

  /**
   * Removes any data from the cache where the key contains the key passed in
   * @param key string used for fuzzy matching
   */
  async removeFuzzy(key: string) {
    const keys = Array.from(this.data.keys()).filter(_key => _key.includes(key));
    keys.forEach(_key => {
      this.data.delete(_key);
      this.loadingPromises.delete(_key);
    });
  }

  /**
   * Clears the cache, including any un resolved promises
   */
  clear() {
    this.data.clear();
    this.loadingPromises.clear();
  }
}
